import React, { useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  MessageCircle,
  CalendarClock,
  Mail,
  Send,
  CheckCircle2,
  Sparkles,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WHATSAPP_HREF =
  "https://wa.me/17865756919?text=Hi%21%20I%27d%20like%20to%20learn%20about%20the%20website%20special.";

const initialForm = {
  name: "",
  business_name: "",
  phone: "",
  email: "",
  current_url: "",
  website_type: "",
  message: "",
};

export default function ContactSection() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.business_name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Please fill in your name, business, phone, and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Thank you! Your website request has been received. We’ll review your business information and contact you shortly.");
      setSubmitted(true);
      setForm(initialForm);
    } catch (err) {
      const msg = "Something went wrong. Please try again or contact us directly at info@instantsiterevival.com.";
      toast.error(msg);
      console.error("Lead submission failed:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 fade-up">
          <div className="inline-flex items-center gap-2 rounded-full bg-yellow-50 text-yellow-800 border border-yellow-200 px-3 py-1 text-[11px] font-black tracking-widest uppercase">
            <Sparkles size={12} /> Free Website Review
          </div>
          <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">
            Want to See What Your Business Could Look Like Online?
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Tell us about your business and we’ll show you how a modern website can help you look
            more professional, build trust, and get more customers to take action.
          </p>

          <div className="mt-8 space-y-3">
            <a
              href={WHATSAPP_HREF}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
              className="flex items-center justify-between rounded-2xl bg-[#25D366] hover:bg-[#1eb558] text-white px-5 py-4 font-bold transition-all hover:-translate-y-0.5 shadow-lg"
            >
              <span className="flex items-center gap-3">
                <span className="h-10 w-10 rounded-xl bg-white/15 inline-flex items-center justify-center">
                  <MessageCircle size={20} />
                </span>
                <span className="flex flex-col text-left">
                  Message Us Now
                  <span className="text-xs font-medium opacity-90">786-575-6919 · WhatsApp</span>
                </span>
              </span>
              <Send size={18} />
            </a>

            <a
              href="mailto:info@instantsiterevival.com?subject=Website%20Revival%20Special&body=Hi%2C%20I%27d%20like%20to%20learn%20about%20the%20%24750%20special."
              data-testid="contact-email-btn"
              className="flex items-center justify-between rounded-2xl bg-slate-900 hover:bg-slate-800 text-white px-5 py-4 font-bold transition-all hover:-translate-y-0.5"
            >
              <span className="flex items-center gap-3">
                <span className="h-10 w-10 rounded-xl bg-white/10 inline-flex items-center justify-center">
                  <Mail size={20} />
                </span>
                <span className="flex flex-col text-left">
                  Email Us
                  <span className="text-xs font-medium text-slate-300">info@instantsiterevival.com</span>
                </span>
              </span>
              <Send size={18} />
            </a>

            <a
              href="https://calendly.com/instantsiterevival-info/30min"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-booking-btn"
              className="flex items-center justify-between rounded-2xl border border-slate-200 hover:border-blue-400 hover:text-blue-700 text-slate-900 px-5 py-4 font-bold transition-all bg-white"
            >
              <span className="flex items-center gap-3">
                <span className="h-10 w-10 rounded-xl bg-blue-50 text-blue-600 inline-flex items-center justify-center">
                  <CalendarClock size={20} />
                </span>
                <span className="flex flex-col text-left">
                  Book a Quick Call
                  <span className="text-xs font-medium text-slate-500">30-minute slot · Calendly</span>
                </span>
              </span>
              <Send size={18} />
            </a>
          </div>
        </div>

        <div className="lg:col-span-7 fade-up">
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            className="rounded-3xl border border-slate-100 bg-gradient-to-b from-slate-50 to-white p-7 sm:p-9 shadow-xl space-y-5"
          >
            {submitted && (
              <div
                data-testid="contact-success-banner"
                className="flex items-start gap-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3"
              >
                <CheckCircle2 size={20} className="mt-0.5 shrink-0" />
                <div>
                  <div className="font-bold">Thank you! Your website request has been received.</div>
                  <div className="text-sm">We’ll review your business information and contact you shortly. Want to chat now? Tap “Message Us Now”.</div>
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name" className="text-slate-700 font-semibold">Name *</Label>
                <Input
                  id="name"
                  name="name"
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Your full name"
                  className="mt-1.5 h-11 bg-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="business_name" className="text-slate-700 font-semibold">Business Name *</Label>
                <Input
                  id="business_name"
                  name="business_name"
                  data-testid="contact-input-business"
                  value={form.business_name}
                  onChange={onChange}
                  placeholder="e.g. Joe’s Auto Shop"
                  className="mt-1.5 h-11 bg-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-slate-700 font-semibold">Phone Number *</Label>
                <Input
                  id="phone"
                  name="phone"
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="(555) 555-5555"
                  className="mt-1.5 h-11 bg-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-slate-700 font-semibold">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="you@business.com"
                  className="mt-1.5 h-11 bg-white"
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="current_url" className="text-slate-700 font-semibold">Current Website URL (optional)</Label>
                <Input
                  id="current_url"
                  name="current_url"
                  data-testid="contact-input-url"
                  value={form.current_url}
                  onChange={onChange}
                  placeholder="https://yourbusiness.com"
                  className="mt-1.5 h-11 bg-white"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="website_type" className="text-slate-700 font-semibold">What kind of website do you need?</Label>
                <Input
                  id="website_type"
                  name="website_type"
                  data-testid="contact-input-type"
                  value={form.website_type}
                  onChange={onChange}
                  placeholder="e.g. Auto shop website, restaurant, contractor, salon..."
                  className="mt-1.5 h-11 bg-white"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="message" className="text-slate-700 font-semibold">Message</Label>
                <Textarea
                  id="message"
                  name="message"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={onChange}
                  placeholder="Tell us a little about your business and what you’re looking for…"
                  className="mt-1.5 min-h-[120px] bg-white"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-btn"
              className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-bold text-base glow-blue"
            >
              {submitting ? "Sending..." : "Request My Website Review"}
            </Button>
            <p className="text-xs text-slate-500 text-center">
              We’ll never share your info. Reply within 24 business hours.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
